/**
 * The 10 signature programs (A–J) for Change Center Mentoring Group.
 * These are the master categories displayed on /programs. Status is editable
 * from the admin dashboard via /api/admin/config/program-status.
 */
import {
  Brain, HeartHandshake, GraduationCap, Landmark, Briefcase,
  Shield, Baby, School, RefreshCw, Megaphone,
} from "lucide-react";

export const STATUS_LABELS = {
  "enrolling": "Enrolling Now",
  "operating": "Currently Operating",
  "pilot": "Pilot Program",
  "coming-soon": "Coming Soon",
  "paused": "Temporarily Paused",
};

export const STATUS_STYLES = {
  "enrolling": "bg-[#F5B90A] text-black",
  "operating": "bg-emerald-600 text-white",
  "pilot": "bg-sky-600 text-white",
  "coming-soon": "bg-black/70 text-white",
  "paused": "bg-black/40 text-white",
};

export const SIGNATURE_PROGRAMS = [
  {
    slug: "change-choices",
    letter: "A",
    title: "Change Choices: Strong Minds",
    tagline: "Strong Minds. Wise Choices. Brighter Futures.",
    signature: true,
    ages: "10–17",
    duration: "24 weeks",
    icon: Brain,
    summary:
      "Our signature 24-week integrated mental wellness, substance-use prevention, and healthy decision-making program for boys ages 10–17.",
    experience:
      "Interactive lessons, age-appropriate discussions, real-world scenarios, mentoring activities, family participation, and connections to qualified professionals when appropriate.",
    curriculum: [
      {
        heading: "Substance Use Prevention",
        items: [
          "Drug and alcohol awareness",
          "Marijuana and cannabis education",
          "Vaping and nicotine prevention",
          "Prescription medication safety",
          "Counterfeit pills and overdose awareness",
          "Peer pressure and refusal skills",
          "Recognizing risky situations",
          "Positive alternatives to substance use",
        ],
      },
      {
        heading: "Mental & Emotional Wellness",
        items: [
          "Emotional awareness and regulation",
          "Understanding stress and anxiety",
          "Coping with sadness and disappointment",
          "Recognizing when extra mental health support is needed",
          "Healthy anger management",
          "Grief and loss",
          "Bullying and cyberbullying",
          "Building self-esteem and confidence",
          "Trauma-aware supportive relationships",
          "Healthy coping and problem-solving",
        ],
      },
      {
        heading: "Leadership & Decision-Making",
        items: [
          "Positive identity and belonging",
          "Personal responsibility",
          "Healthy relationships",
          "Communication skills",
          "Goal setting and future planning",
          "Conflict resolution",
          "Making safe and informed choices",
        ],
      },
    ],
    disclaimer:
      "This is an educational and preventive mentoring program. Change Center does not provide clinical diagnosis, psychotherapy, or medical treatment. When needed, we help connect families to qualified community resources.",
    ctas: [
      { label: "Learn About Strong Minds", to: "/strong-minds" },
      { label: "Enroll Your Child", to: "/enroll" },
      { label: "Request For Your School", to: "/partnerships" },
      { label: "Sponsor This Program", to: "/donate" },
    ],
  },
  {
    slug: "core-mentorship",
    letter: "B",
    title: "Core Mentorship & Character Development",
    tagline: "The foundation of every young man we serve.",
    ages: "10–17",
    icon: HeartHandshake,
    summary:
      "Our foundational mentoring experience helps boys develop confidence, positive character, responsibility, integrity, self-discipline, and leadership skills.",
    experience:
      "One-on-one and group mentoring within strict safeguarding policies, accountability, and positive male role models.",
    curriculum: [
      {
        heading: "What's Covered",
        items: [
          "One-on-one and group mentoring",
          "Accountability and responsible decision-making",
          "Positive male role models",
          "Personal development",
          "Social skills and communication",
          "Goal setting",
          "Community responsibility",
          "Personal presentation and confidence",
          "Respect for self and others",
        ],
      },
    ],
  },
  {
    slug: "academic-success",
    letter: "C",
    title: "Academic Success & Educational Support",
    tagline: "Every young man deserves a path to graduation.",
    ages: "10–17",
    icon: GraduationCap,
    summary:
      "Tutoring, study habits, academic accountability, homework support, reading development, computer literacy, and educational encouragement.",
    experience:
      "After-school homework help, reading circles, and school-partnership programming built around each student's needs.",
    curriculum: [
      {
        heading: "What's Covered",
        items: [
          "Tutoring and homework support",
          "Study habits and academic accountability",
          "Reading development",
          "Computer literacy and digital skills",
          "Educational encouragement",
          "School-partnership inquiry opportunities",
        ],
      },
    ],
  },
  {
    slug: "financial-entrepreneurship",
    letter: "D",
    title: "Financial Literacy & Entrepreneurship",
    tagline: "Money mindset. Business mindset. Future mindset.",
    ages: "10–17",
    icon: Landmark,
    summary:
      "Help young men understand money management, saving, budgeting, banking, responsible spending, and the fundamentals of entrepreneurship.",
    experience:
      "Hands-on activities: creating sample business ideas, real budgets, savings challenges, and guest sessions with local entrepreneurs.",
    curriculum: [
      {
        heading: "What's Covered",
        items: [
          "Money management",
          "Saving and budgeting",
          "Banking fundamentals",
          "Responsible spending",
          "Entrepreneurship",
          "Business planning",
          "Career exploration",
          "Financial responsibility",
        ],
      },
    ],
  },
  {
    slug: "workforce-readiness",
    letter: "E",
    title: "Workforce Readiness & Career Development",
    tagline: "Ready for the first job. Built for the long career.",
    ages: "13–17",
    icon: Briefcase,
    summary:
      "Resume building, interview prep, professional communication, workplace expectations, career exploration, and leadership at work.",
    experience:
      "Mock interviews, dress-for-success workshops, portfolio reviews, and introductions to local employers and industries.",
    curriculum: [
      {
        heading: "What's Covered",
        items: [
          "Resume building",
          "Interview preparation",
          "Professional communication",
          "Workplace expectations",
          "Career exploration",
          "Technology and computer skills",
          "Graphic design and digital media",
          "Entrepreneurship and business exposure",
          "Leadership in the workplace",
          "Employment readiness",
        ],
      },
    ],
  },
  {
    slug: "conflict-resolution",
    letter: "F",
    title: "Conflict Resolution & Positive Decisions",
    tagline: "Peace is a skill. We teach it.",
    ages: "10–17",
    icon: Shield,
    summary:
      "Anger management, nonviolent conflict resolution, peer-pressure resistance, accountability, and positive interactions with authority.",
    experience:
      "Role-play, restorative circles, de-escalation drills, and conversations with community leaders and law enforcement.",
    curriculum: [
      {
        heading: "What's Covered",
        items: [
          "Anger management",
          "Nonviolent conflict resolution",
          "Peer-pressure resistance",
          "Accountability",
          "Respect for authority and others",
          "Consequences of decisions",
          "Positive interactions with law enforcement",
          "Communication during difficult situations",
          "De-escalation strategies",
        ],
      },
    ],
  },
  {
    slug: "little-leaders",
    letter: "G",
    title: "Little Leaders, Big Choices",
    tagline: "Starting strong, starting early.",
    ages: "5–9",
    icon: Baby,
    summary:
      "Our early-development track for younger boys — understanding feelings, safe choices, kindness, and the building blocks of leadership.",
    experience:
      "Story-based lessons, play-based learning, caregiver participation, and gentle introduction to community.",
    curriculum: [
      {
        heading: "What's Covered",
        items: [
          "Understanding feelings",
          "Safe choices",
          "Respect and kindness",
          "Following instructions",
          "Positive friendships",
          "Healthy habits",
          "Identifying trusted adults",
          "Early leadership skills",
        ],
      },
    ],
  },
  {
    slug: "school-based",
    letter: "H",
    title: "School-Based Mentoring",
    tagline: "Bringing Change Center into the classroom.",
    ages: "All ages",
    icon: School,
    summary:
      "We work with schools across Clayton County and surrounding districts to bring mentorship, prevention education, and life skills directly to students.",
    experience:
      "In-school small groups, assemblies, lunch mentoring, and after-school programming — tailored to each school partner.",
    curriculum: [
      {
        heading: "Partnership Features",
        items: [
          "On-site small-group mentoring",
          "Classroom prevention education",
          "Assemblies and guest sessions",
          "Lunch-period mentoring",
          "After-school programming",
          "Teacher and counselor coordination",
          "Parent engagement nights",
        ],
      },
    ],
    ctas: [
      { label: "Bring Change Center to Your School", to: "/partnerships" },
      { label: "Partner With Us", to: "/partnerships" },
    ],
  },
  {
    slug: "turnaround-project",
    letter: "I",
    title: "The Turnaround Project",
    tagline: "12 weeks that change everything.",
    ages: "10–17",
    icon: RefreshCw,
    summary:
      "A 12-week positive decision-making, accountability, and leadership mentoring initiative for young men ready to turn things around.",
    experience:
      "Weekly small-group sessions, one-on-one check-ins, family touchpoints, and a culminating leadership project.",
    curriculum: [
      {
        heading: "Focus Areas",
        items: [
          "Consequences of decisions",
          "Healthy communication",
          "Healthy relationships",
          "Conflict prevention",
          "Positive community interactions",
          "Leadership and goal setting",
        ],
      },
    ],
    ctas: [
      { label: "Request Information", to: "/contact" },
      { label: "Enroll Your Child", to: "/enroll" },
      { label: "Learn About the Turnaround Toolkit", to: "/turnaround" },
    ],
  },
  {
    slug: "youth-leadership",
    letter: "J",
    title: "Youth Leadership & Community Service",
    tagline: "Leaders are built by serving others.",
    ages: "10–17",
    icon: Megaphone,
    summary:
      "Opportunities to serve the community, develop leadership skills, and become positive peer role models.",
    experience:
      "Community service Saturdays, peer-mentoring rotations, public speaking practice, and supervised youth-led events.",
    curriculum: [
      {
        heading: "What Participants Do",
        items: [
          "Community service projects",
          "Become peer prevention ambassadors",
          "Public speaking practice",
          "Develop leadership skills",
          "Organize positive youth activities",
          "Participate in supervised community events",
          "Become positive role models",
        ],
      },
    ],
  },
];

export const PROGRAM_BY_SLUG = Object.fromEntries(SIGNATURE_PROGRAMS.map((p) => [p.slug, p]));
